import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const SPECIAL_ROUTES = {
  services: '/services',
  gallery: '/gallery',
  team: '/doctor-profile',
  reviews: '/reviews'
};

const scrollToId = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

const useIsScrolled = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return isScrolled;
};

const DesktopNavButton = ({ item, isScrolled, onClick }) => (
  <button
    onClick={onClick}
    className="transition-all duration-300 font-semibold relative group"
    style={{
      color: isScrolled ? '#1e293b' : '#ffffff',
      textShadow: isScrolled ? 'none' : '0 2px 4px rgba(0,0,0,0.3)'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.color = 'var(--medical-accent)';
      e.currentTarget.style.transform = 'translateY(-2px)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.color = isScrolled ? '#1e293b' : '#ffffff';
      e.currentTarget.style.transform = 'translateY(0)';
    }}
    data-testid={`nav-${item.id}`}
  >
    {item.label}
    <span
      className="absolute bottom-0 left-0 w-0 h-0.5 transition-all duration-300 group-hover:w-full"
      style={{ backgroundColor: isScrolled ? 'var(--medical-primary)' : '#ffffff' }}
    ></span>
  </button>
);

const MobileNavButton = ({ item, onClick }) => (
  <button
    onClick={onClick}
    className="block w-full text-left px-4 py-3 text-slate-700 transition-all duration-300"
    onMouseEnter={(e) => {
      e.currentTarget.style.backgroundColor = '#f0fdf4';
      e.currentTarget.style.color = 'var(--medical-primary)';
      e.currentTarget.style.paddingLeft = '20px';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = '';
      e.currentTarget.style.color = '';
      e.currentTarget.style.paddingLeft = '16px';
    }}
    data-testid={`mobile-nav-${item.id}`}
  >
    {item.label}
  </button>
);

const LogoButton = ({ isScrolled, onClick, label }) => (
  <button
    onClick={onClick}
    className="text-xl font-bold transition-all duration-300"
    style={{
      color: isScrolled ? 'var(--medical-primary)' : '#ffffff',
      textShadow: isScrolled ? 'none' : '0 2px 4px rgba(0,0,0,0.3)'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.color = 'var(--medical-accent)';
      e.currentTarget.style.transform = 'scale(1.05)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.color = isScrolled ? 'var(--medical-primary)' : '#ffffff';
      e.currentTarget.style.transform = 'scale(1)';
    }}
    data-testid="header-logo"
  >
    {label}
  </button>
);

const MobileMenu = ({ navItems, onNavigate }) => (
  <div
    className="md:hidden mt-4 py-4 bg-white rounded-lg shadow-lg animate-slide-up"
    style={{
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
    }}
  >
    {navItems.map((item) => (
      <MobileNavButton key={item.id} item={item} onClick={() => onNavigate(item.id)} />
    ))}
  </div>
);

const Header = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isScrolled = useIsScrolled();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleNavigation = useCallback((id) => {
    setIsMobileMenuOpen(false);
    if (SPECIAL_ROUTES[id]) return navigate(SPECIAL_ROUTES[id]);
    if (location.pathname === '/') return scrollToId(id);
    navigate('/');
    setTimeout(() => scrollToId(id), 100);
  }, [location.pathname, navigate]);

  const navItems = [
    { id: 'about', label: t('header.about') },
    { id: 'services', label: t('header.services') },
    { id: 'gallery', label: t('header.gallery', 'Gallery') },
    { id: 'team', label: t('header.team') },
    { id: 'reviews', label: t('header.reviews', 'Reviews') },
    { id: 'location', label: t('header.location') },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'glass-effect shadow-lg' : 'bg-transparent'
      }`}
      style={{
        backdropFilter: isScrolled ? 'blur(20px)' : 'none',
        WebkitBackdropFilter: isScrolled ? 'blur(20px)' : 'none',
      }}
    >
      <nav className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <LogoButton isScrolled={isScrolled} onClick={() => navigate('/')} label={t('hero.title')} />

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <DesktopNavButton key={item.id} item={item} isScrolled={isScrolled} onClick={() => handleNavigation(item.id)} />
            ))}
            <LanguageSwitcher isScrolled={isScrolled} />
          </div>

          {/* Mobile Menu Button & Language Switcher */}
          <div className="flex items-center gap-3 md:hidden">
            <LanguageSwitcher />
            <Button
              variant="ghost"
              size="icon"
              className="transition-transform duration-300 hover:scale-110"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && <MobileMenu navItems={navItems} onNavigate={handleNavigation} />}
      </nav>
    </header>
  );
};

export default Header;
